import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Input from "components/DesignSystem/Input/Input";
import Loader from "components/DesignSystem/Loader/loader";
import { MobileInput } from "components/DesignSystem/MobileInput/MobileInput";
import Modal from "components/DesignSystem/Modal/Modal";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import {
  BOOKKEEPING_METHOD_OPTIONS,
  DOCS_IN_HAND_OPTIONS,
  RESIDENCY_OPTIONS,
} from "constants/handoverDetails";
import { YES_NO_OPTIONS } from "constants/churnDetails";
import { FormikForm } from "components/FormikForm/FormikForm";
import { handoverDetailsSchema } from "formValidations/handoverDetailsSchema";
import { Field, FieldProps } from "formik";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { filterOptionsByValues } from "util/filterOptionsByValues";
import { findOptionByValue } from "util/findOptionByValue";
import { getUserShortName } from "util/getUserShortName";
import { uniqueByValue } from "util/uniqueByValue";
import { formatDate } from "utils/formatDate";
import {
  HandoverDetailFormValues,
  HandoverDetailPayload,
} from "store/apis/salesCrm";
import { AssignableAgent, useGetAssignAgentsQuery } from "store/apis/agents";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { useGetCountryStatesQuery } from "store/apis/teamSetting";
import { US_UUID } from "constants/regAgent";
import { Subscription } from "types/Models/subscription";
import { HandoverAttachmentField } from "./HandoverAttachmentField";
import { useHandoverDetailsFormData } from "./useHandoverDetailsFormData";

const EMPTY_SUBSCRIPTIONS: Subscription[] = [];

type CountryState = { name: string; country: string; state_id: string };
const EMPTY_COUNTRY_STATES: CountryState[] = [];

const EMPTY_SALES_AGENTS: AssignableAgent[] = [];

const statesAnsweredYes = (
  foreignQualification: Record<string, string>
): string[] =>
  Object.entries(foreignQualification)
    .filter(([, value]) => value === "yes")
    .map(([state]) => state);

type HandoverDetailsModalProps = {
  isOpen: boolean;
  close: () => void;
  groupId: string;
  onSubmitted?: () => void;
};

type CompanyProfileSectionProps = {
  statesOfOperationOptions: OptionData[];
  statesOfOperationDefaultValue: OptionData[];
  docsInHandDefaultValue: OptionData[];
  bookkeepingMethodDefaultValue: OptionData | null;
  selectedStatesOfOperation: string[];
  foreignQualification: Record<string, string>;
  onForeignQualificationChange: (state: string, value: string) => void;
  extensionFiled: string;
  onExtensionFiledChange: (value: string) => void;
};

const SectionHeading = ({ children }: { children: string }) => (
  <h3 className="t-col-span-2 t-mb-0 t-mt-2 t-text-subtitle t-text-text-100 first:t-mt-0">
    {children}
  </h3>
);

const YesNoField = ({
  name,
  label,
  value,
  onValueChange,
  disabled = false,
  required = false,
}: {
  name: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
}) => (
  <div>
    <Label htmlFor={name} required={required}>
      {label}
    </Label>
    <Radio.Root
      name={name}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <Radio.Content>
        <div className="t-flex t-items-center t-gap-2">
          {YES_NO_OPTIONS.map((option) => (
            <Radio.Item
              asChild
              className="!t-w-auto"
              disabled={disabled}
              value={option.value}
              key={option.value}
            >
              <div className="t-text-body-sm t-text-text-100">
                {option.label}
              </div>
            </Radio.Item>
          ))}
        </div>
      </Radio.Content>
    </Radio.Root>
  </div>
);

const ContactAndBasicsSection = ({
  founderResidencyDefaultValue,
  closedByOptions,
  closedByDefaultValue,
}: {
  founderResidencyDefaultValue: OptionData | null;
  closedByOptions: OptionData[];
  closedByDefaultValue: OptionData | null;
}) => (
  <>
    <SectionHeading>Contact &amp; Basics</SectionHeading>
    <Input
      withForm
      disabled
      name="customer_name"
      label="Customer Name"
      placeholder="None"
    />
    <Combobox
      withForm
      name="closed_by"
      label="Closed By"
      options={closedByOptions}
      defaultValue={closedByDefaultValue}
      placeholder="Select"
      menuPlacement="auto"
      maxMenuHeight={220}
    />
    <Field name="handover_date">
      {({ field }: FieldProps) => (
        <DateInput
          {...field}
          disabled
          label="Handover Date"
          placeholder="None"
        />
      )}
    </Field>
    <Input
      withForm
      name="founder_name"
      label="Founder Name"
      placeholder="Enter founder name"
    />
    <Input
      withForm
      name="founder_email"
      label="Founder Email"
      placeholder="Enter founder email"
    />
    <MobileInput name="founder_phone" label="Founder Phone" />
    <Combobox
      withForm
      name="founder_residency"
      label="Founder Residency"
      options={RESIDENCY_OPTIONS}
      defaultValue={founderResidencyDefaultValue}
      placeholder="Select"
      menuPlacement="auto"
      maxMenuHeight={220}
    />
    <Input
      withForm
      name="lead_source"
      label="Lead Source"
      placeholder="Enter lead source"
    />
  </>
);

const ServicesSoldSection = ({
  servicesSoldOptions,
  servicesSoldDefaultValue,
}: {
  servicesSoldOptions: OptionData[];
  servicesSoldDefaultValue: OptionData[];
}) => (
  <>
    <SectionHeading>Services Sold &amp; Quote</SectionHeading>
    <div className="t-col-span-2">
      <Combobox
        withForm
        isMulti
        name="services_sold"
        label="Services Sold"
        options={servicesSoldOptions}
        defaultValue={servicesSoldDefaultValue}
        placeholder="Select services sold"
        menuPlacement="auto"
        maxMenuHeight={220}
      />
    </div>
    <Input
      withForm
      name="quote_link"
      label="Quote Link"
      placeholder="Enter quote link"
    />
    <Input
      withForm
      name="fireflies_link"
      label="Fireflies Link"
      placeholder="Enter fireflies link"
    />
  </>
);

const ForeignQualificationFields = ({
  selectedStatesOfOperation,
  foreignQualification,
  onForeignQualificationChange,
}: {
  selectedStatesOfOperation: string[];
  foreignQualification: Record<string, string>;
  onForeignQualificationChange: (state: string, value: string) => void;
}) => {
  if (selectedStatesOfOperation.length === 0) return null;

  return (
    <div className="t-col-span-2 t-flex t-flex-col t-gap-3">
      {selectedStatesOfOperation.map((state) => (
        <YesNoField
          key={state}
          name={`foreign_qualification.${state}`}
          label={`${state} - Foreign Qualification`}
          value={foreignQualification[state] ?? "no"}
          onValueChange={(value) => onForeignQualificationChange(state, value)}
        />
      ))}
    </div>
  );
};

const CompanyProfileSection = ({
  statesOfOperationOptions,
  statesOfOperationDefaultValue,
  docsInHandDefaultValue,
  bookkeepingMethodDefaultValue,
  selectedStatesOfOperation,
  foreignQualification,
  onForeignQualificationChange,
  extensionFiled,
  onExtensionFiledChange,
}: CompanyProfileSectionProps) => (
  <>
    <SectionHeading>Company Profile</SectionHeading>
    <Field name="incorporated_date">
      {({ field }: FieldProps) => (
        <DateInput
          {...field}
          portalId="incorporated_date"
          label="Incorporated Date"
          placeholder="Select date"
        />
      )}
    </Field>
    <Input
      withForm
      name="incorporated_state"
      label="Incorporated State"
      placeholder="Enter incorporated state"
    />
    <Input
      withForm
      name="us_activity"
      label="US Activity"
      placeholder="Enter US activity"
    />
    <Input
      withForm
      name="employees"
      label="Employees"
      placeholder="Enter employees"
    />
    <Combobox
      withForm
      name="bookkeeping_method"
      label="Bookkeeping Method"
      options={BOOKKEEPING_METHOD_OPTIONS}
      defaultValue={bookkeepingMethodDefaultValue}
      placeholder="Select"
      menuPlacement="auto"
      maxMenuHeight={220}
    />
    <div className="t-col-span-2">
      <Combobox
        withForm
        isMulti
        name="states_of_operation"
        label="States of Operation"
        options={statesOfOperationOptions}
        defaultValue={statesOfOperationDefaultValue}
        placeholder="Select"
        menuPlacement="auto"
        maxMenuHeight={220}
      />
    </div>
    <ForeignQualificationFields
      selectedStatesOfOperation={selectedStatesOfOperation}
      foreignQualification={foreignQualification}
      onForeignQualificationChange={onForeignQualificationChange}
    />
    <div className="t-col-span-2">
      <TextArea
        name="shareholding_pattern"
        label="Shareholding Pattern"
        placeholder="Enter shareholding pattern"
      />
    </div>
    <div className="t-col-span-2">
      <Combobox
        withForm
        isMulti
        name="docs_in_hand"
        label="Docs in Hand"
        options={DOCS_IN_HAND_OPTIONS}
        defaultValue={docsInHandDefaultValue}
        placeholder="Select"
        menuPlacement="auto"
        maxMenuHeight={220}
      />
    </div>
    <YesNoField
      name="extension_filed"
      label="Extension Filed for Current Season"
      value={extensionFiled}
      onValueChange={onExtensionFiledChange}
    />
  </>
);

const AdditionalNotesSection = ({ groupId }: { groupId: string }) => (
  <>
    <SectionHeading>Others</SectionHeading>
    <div className="t-col-span-2">
      <TextArea
        name="watch_outs"
        label="Watch-Outs"
        placeholder="Anything the ops team should watch out for?"
      />
    </div>

    <div className="t-col-span-2">
      <TextArea
        name="confirm_on_call"
        label="Confirm on Onboarding Call"
        placeholder="What should be confirmed on the onboarding call?"
      />
    </div>

    <div className="t-col-span-2">
      <HandoverAttachmentField groupId={groupId} />
    </div>
  </>
);

export const HandoverDetailsModal = ({
  isOpen,
  close,
  groupId,
  onSubmitted,
}: HandoverDetailsModalProps) => {
  const { successToast, alertToast } = useToast();
  const {
    isLoadingExistingData,
    initialValues,
    submitHandoverDetail,
    isSubmitting,
    submittedByName,
    submittedAt,
    hasMovedToOps,
  } = useHandoverDetailsFormData(isOpen, groupId);
  const {
    data: subscriptions = EMPTY_SUBSCRIPTIONS,
    isFetching: isLoadingSubscriptions,
  } = useGetSubscriptionsQuery({}, { skip: !isOpen });
  const {
    data: countryStates = EMPTY_COUNTRY_STATES,
    isFetching: isLoadingCountryStates,
  } = useGetCountryStatesQuery({ countryId: US_UUID }, { skip: !isOpen });
  const {
    data: salesAgents = EMPTY_SALES_AGENTS,
    isFetching: isLoadingSalesAgents,
  } = useGetAssignAgentsQuery(
    { agentType: "SALES_EXECUTIVE" },
    { skip: !isOpen }
  );

  // Combobox only reads `defaultValue` at mount, not live Formik state, so every prefilled selection needs one computed here; the isLoadingForm gate below keeps every option-dependent field (states_of_operation, services_sold) from mounting before its source data is ready.
  const isLoadingForm =
    isLoadingExistingData ||
    isLoadingSubscriptions ||
    isLoadingCountryStates ||
    isLoadingSalesAgents;
  const statesOfOperationOptions = countryStates.map((state) => ({
    value: state.name,
    label: state.name,
  }));
  const defaultStatesOfOperationValues = filterOptionsByValues(
    statesOfOperationOptions,
    initialValues.states_of_operation
  );
  const defaultDocsInHandValues = filterOptionsByValues(
    DOCS_IN_HAND_OPTIONS,
    initialValues.docs_in_hand
  );
  const servicesSoldOptions = uniqueByValue(
    subscriptions.map((subscription) => ({
      value: subscription.subscription_name,
      label: subscription.subscription_name,
    }))
  );
  const defaultServicesSoldValues = filterOptionsByValues(
    servicesSoldOptions,
    initialValues.services_sold
  );
  const defaultBookkeepingMethod = findOptionByValue(
    BOOKKEEPING_METHOD_OPTIONS,
    initialValues.bookkeeping_method
  );
  const defaultFounderResidency = findOptionByValue(
    RESIDENCY_OPTIONS,
    initialValues.founder_residency
  );
  // closed_by is a plain name string, not an agent id, so options are keyed by name.
  const closedByOptions = uniqueByValue(
    salesAgents.map(({ name }) => ({ value: name, label: name }))
  );
  const defaultClosedBy = findOptionByValue(
    closedByOptions,
    getUserShortName(initialValues.closed_by)
  );

  const buildPayload = (
    values: HandoverDetailFormValues
  ): HandoverDetailPayload => {
    // A deselected state can leave a stale "yes" in foreign_qualification, so only submit answers for states still selected.
    const yesStates = statesAnsweredYes(values.foreign_qualification).filter(
      (state) => values.states_of_operation.includes(state)
    );
    // founder_phone_country is local-only state for MobileInput's country picker, never part of the backend contract.
    const { founder_phone_country, ...scalarValues } = values;

    return {
      ...scalarValues,
      states_of_operation: values.states_of_operation.join("\n"),
      docs_in_hand: values.docs_in_hand.join("\n"),
      services_sold: values.services_sold.join("\n"),
      foreign_qualification: yesStates.join("\n"),
    };
  };

  const handleSubmit = async (values: HandoverDetailFormValues) => {
    try {
      await submitHandoverDetail({
        groupId,
        payload: buildPayload(values),
      }).unwrap();
      successToast({ message: "Handover details saved" });
      onSubmitted?.();
      close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="xl" useCustomOverlay>
        <FormikForm<HandoverDetailFormValues>
          initialValues={initialValues}
          enableReinitialize
          validationSchema={handoverDetailsSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, submitForm }) => {
            const handleExtensionFiledChange = (value: string) =>
              setFieldValue("extension_filed", value);

            const handleForeignQualificationChange = (
              state: string,
              value: string
            ) =>
              setFieldValue("foreign_qualification", {
                ...values.foreign_qualification,
                [state]: value,
              });

            return (
              <>
                <Modal.Header>
                  <Modal.Title>Handover Details</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-max-h-[60vh] t-overflow-y-scroll">
                  {isLoadingForm ? (
                    <Loader size="small" />
                  ) : (
                    <>
                      <div className="t-grid t-grid-cols-2 t-gap-4">
                        <ContactAndBasicsSection
                          founderResidencyDefaultValue={defaultFounderResidency}
                          closedByOptions={closedByOptions}
                          closedByDefaultValue={defaultClosedBy}
                        />
                        <ServicesSoldSection
                          servicesSoldOptions={servicesSoldOptions}
                          servicesSoldDefaultValue={defaultServicesSoldValues}
                        />
                        <CompanyProfileSection
                          statesOfOperationOptions={statesOfOperationOptions}
                          statesOfOperationDefaultValue={
                            defaultStatesOfOperationValues
                          }
                          docsInHandDefaultValue={defaultDocsInHandValues}
                          bookkeepingMethodDefaultValue={
                            defaultBookkeepingMethod
                          }
                          selectedStatesOfOperation={values.states_of_operation}
                          foreignQualification={values.foreign_qualification}
                          onForeignQualificationChange={
                            handleForeignQualificationChange
                          }
                          extensionFiled={values.extension_filed}
                          onExtensionFiledChange={handleExtensionFiledChange}
                        />
                        <AdditionalNotesSection groupId={groupId} />
                      </div>
                      {submittedByName && (
                        <p className="t-mt-4 t-text-body-sm t-text-text-60">
                          Last submitted by {submittedByName}
                          {submittedAt ? ` on ${formatDate(submittedAt)}` : ""}
                        </p>
                      )}
                    </>
                  )}
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button type="button">Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    type="button"
                    customType="primary"
                    isLoading={isSubmitting}
                    disabled={isLoadingForm || isSubmitting}
                    onClick={submitForm}
                  >
                    Submit
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
